import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productsApi, ProductDetail, Offer, ProductOption } from '@/api/products';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  ShoppingBag,
  Ticket,
  Settings,
  Plus,
  School,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export function ProductDetailPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOfferDialog, setDeleteOfferDialog] = useState<Offer | null>(null);
  const [deleteOptionDialog, setDeleteOptionDialog] = useState<ProductOption | null>(null);

  const isAdmin = hasRole('ROLE_ADMIN') || hasRole('ROLE_SUPERADMIN');

  useEffect(() => {
    if (code) {
      fetchProduct(code);
    }
  }, [code]);

  const fetchProduct = async (productCode: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await productsApi.getByCode(productCode);
      setProduct(data);
    } catch (err) {
      setError('Erreur lors du chargement du produit');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!product) return;

    try {
      setDeleting(true);
      await productsApi.delete(product.code);
      navigate('/products');
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la suppression du produit');
      setShowDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteOffer = async () => {
    if (!deleteOfferDialog || !product) return;

    try {
      await productsApi.deleteOffer(product.code, deleteOfferDialog.code);
      fetchProduct(product.code);
      setDeleteOfferDialog(null);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la suppression de l\'offre');
    }
  };

  const handleDeleteOption = async () => {
    if (!deleteOptionDialog || !product) return;

    try {
      await productsApi.deleteOption(product.code, deleteOptionDialog.id);
      fetchProduct(product.code);
      setDeleteOptionDialog(null);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la suppression de l\'option');
    }
  };

  const formatPrice = (price: number | null) => {
    if (price === null || price === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/products')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux produits
        </Button>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error || 'Produit non trouve'}
        </div>
      </div>
    );
  }

  const recurringOptions = product.options.filter(o => o.type === 'recurring');
  const oneShotOptions = product.options.filter(o => o.type === 'one_shot');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" asChild>
              <Link to="/products">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ShoppingBag className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              {product.name}
            </h1>
            <Badge variant={product.isActive ? 'default' : 'secondary'} className="ml-2">
              {product.isActive ? 'Actif' : 'Inactif'}
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to={`/products/${product.code}/edit`}>
              <Pencil className="h-4 w-4" />
              Modifier
            </Link>
          </Button>
          {isAdmin && (
            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </Button>
          )}
        </div>
      </div>

      {product.description && (
        <p className="text-muted-foreground">{product.description}</p>
      )}

      {/* Offers Section */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30 flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Ticket className="h-4 w-4 text-primary" />
            Offres ({product.offers.length})
          </CardTitle>
          {isAdmin && (
            <Button size="sm" asChild>
              <Link to={`/products/${product.code}/offers/new`}>
                <Plus className="h-4 w-4" />
                Ajouter
              </Link>
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {product.offers.length === 0 ? (
            <p className="text-muted-foreground text-sm p-4">
              Aucune offre definie pour ce produit
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Offre</TableHead>
                    <TableHead className="text-center">Modules</TableHead>
                    <TableHead className="text-right">0-100</TableHead>
                    <TableHead className="text-right">101-250</TableHead>
                    <TableHead className="text-right">251-500</TableHead>
                    <TableHead className="text-right">501+</TableHead>
                    <TableHead className="text-center">Écoles</TableHead>
                    <TableHead className="text-right w-[1%] whitespace-nowrap">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {product.offers.map((offer) => (
                    <TableRow key={offer.id}>
                      <TableCell>
                        <span className="font-medium">{offer.name}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{offer.moduleCount}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatPrice(offer.priceT1)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatPrice(offer.priceT2)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatPrice(offer.priceT3)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatPrice(offer.priceT4)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{offer.schoolCount}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            asChild
                          >
                            <Link to={`/products/${product.code}/offers/${offer.code}`}>
                              <Pencil className="h-4 w-4" />
                            </Link>
                          </Button>
                          {isAdmin && offer.schoolCount === 0 && (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => setDeleteOfferDialog(offer)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Options Section */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30 flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings className="h-4 w-4 text-primary" />
            Options ({product.options.length})
          </CardTitle>
          {isAdmin && (
            <Button size="sm" asChild>
              <Link to={`/products/${product.code}/options/new`}>
                <Plus className="h-4 w-4" />
                Ajouter
              </Link>
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {product.options.length === 0 ? (
            <p className="text-muted-foreground text-sm p-4">
              Aucune option definie pour ce produit
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Option</TableHead>
                    <TableHead className="text-center">Type</TableHead>
                    <TableHead className="text-right">0-100</TableHead>
                    <TableHead className="text-right">101-250</TableHead>
                    <TableHead className="text-right">251-500</TableHead>
                    <TableHead className="text-right">501+</TableHead>
                    <TableHead className="text-right w-[1%] whitespace-nowrap">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recurringOptions.length > 0 && (
                    <>
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={7} className="font-semibold text-sm">
                          Options recurrentes
                        </TableCell>
                      </TableRow>
                      {recurringOptions.map((option) => (
                        <TableRow key={option.id}>
                          <TableCell>
                            <span className="font-medium">{option.name}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">Recurrent</Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatPrice(option.price)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatPrice(option.priceT2)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatPrice(option.priceT3)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatPrice(option.priceT4)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                asChild
                              >
                                <Link to={`/products/${product.code}/options/${option.id}`}>
                                  <Pencil className="h-4 w-4" />
                                </Link>
                              </Button>
                              {isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => setDeleteOptionDialog(option)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                  {oneShotOptions.length > 0 && (
                    <>
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={7} className="font-semibold text-sm">
                          Options one-shot
                        </TableCell>
                      </TableRow>
                      {oneShotOptions.map((option) => (
                        <TableRow key={option.id}>
                          <TableCell>
                            <span className="font-medium">{option.name}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">One-shot</Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatPrice(option.price)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatPrice(option.priceT2)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatPrice(option.priceT3)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatPrice(option.priceT4)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                asChild
                              >
                                <Link to={`/products/${product.code}/options/${option.id}`}>
                                  <Pencil className="h-4 w-4" />
                                </Link>
                              </Button>
                              {isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => setDeleteOptionDialog(option)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schools Section */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="flex items-center gap-2 text-base">
            <School className="h-4 w-4 text-primary" />
            Écoles associées ({product.schools?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!product.schools || product.schools.length === 0 ? (
            <p className="text-muted-foreground text-sm p-4">
              Aucune école associée à ce produit
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>École</TableHead>
                    <TableHead>Offre</TableHead>
                    <TableHead className="text-right w-[1%] whitespace-nowrap">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {product.schools.map((school) => (
                    <TableRow key={school.id}>
                      <TableCell>
                        <span className="font-medium">{school.name}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{school.offerName}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          asChild
                        >
                          <Link to={`/schools/${school.code}`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete product dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le produit ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer le produit{' '}
              <strong>{product.name}</strong> ({product.code}) ?
              <br />
              <br />
              Cette action supprimera egalement toutes les offres et options associees.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete offer dialog */}
      <AlertDialog open={!!deleteOfferDialog} onOpenChange={() => setDeleteOfferDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'offre ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer l'offre{' '}
              <strong>{deleteOfferDialog?.name}</strong> ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOffer}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete option dialog */}
      <AlertDialog open={!!deleteOptionDialog} onOpenChange={() => setDeleteOptionDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'option ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer l'option{' '}
              <strong>{deleteOptionDialog?.name}</strong> ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOption}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
